import React, { useEffect, useState } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Link, useNavigate } from "react-router-dom";
import productAlt1 from "../../images/product-img-1.jpg";
import productAlt2 from "../../images/product-img-2.jpg";
import productAlt3 from "../../images/product-img-3.jpg";
import { MagnifyingGlass } from "react-loader-spinner";
import ScrollToTop from "../ScrollToTop";
import {
  getDeliveryRuleLabel,
  loadPrimaryStorefrontProduct,
  loadRelatedStorefrontProducts,
} from "../../data/storefront";
import { formatProductAmount, getDefaultCartAmountForProduct, getWeightStepKg, isWeightedProduct } from "../../data/catalog";
import { addProductToCart, getSuggestedCartAmount } from "../../data/cart";

const SingleShop = () => {
  const navigate = useNavigate();
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchProduct = async () => {
      const primary = await loadPrimaryStorefrontProduct();
      const related = await loadRelatedStorefrontProducts(4, primary?.id);

      if (!isMounted) {
        return;
      }

      setProduct(primary);
      setQuantity(getSuggestedCartAmount(primary));
      setRelatedProducts(related);
      setTimeout(() => {
        if (isMounted) {
          setLoaderStatus(false);
        }
      }, 1200);
    };

    fetchProduct();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!product && !loaderStatus) {
    return null;
  }

  const handleAddToCart = async (targetProduct, targetQuantity = 1) => {
    try {
      await addProductToCart(targetProduct, targetQuantity);
      navigate("/ShopCart");
    } catch (error) {
      window.alert(error.message);
    }
  };

  const isWeighted = isWeightedProduct(product);
  const quantityStep = product ? getWeightStepKg(product) : 1;

  return (
    <div>
      <div>
        {loaderStatus ? (
          <div className="loader-container">
            <MagnifyingGlass visible={true} height="100" width="100" ariaLabel="magnifying-glass-loading" wrapperStyle={{}} wrapperclassName="magnifying-glass-wrapper" glassColor="#c0efff" color="#0aad0a" />
          </div>
        ) : (
          <>
            <ScrollToTop />

            <section className="my-lg-14 my-8">
              <div className="container">
                <div className="row g-5">
                  <div className="col-lg-6">
                    <div className="border rounded-3 p-3 bg-white">
                      <img src={product.image} alt={product.name} className="img-fluid rounded-3 w-100" style={{ maxHeight: "520px", objectFit: "cover" }} />
                    </div>
                    <div className="row mt-3 g-3">
                      <div className="col-4"><img src={productAlt1} alt="detail produit" className="img-fluid rounded-3 border" /></div>
                      <div className="col-4"><img src={productAlt2} alt="detail produit" className="img-fluid rounded-3 border" /></div>
                      <div className="col-4"><img src={productAlt3} alt="detail produit" className="img-fluid rounded-3 border" /></div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-2"><span className="badge bg-danger">{product.badge}</span></div>
                    <div className="text-small mb-2">
                      <Link to="/Shop" className="text-decoration-none text-muted"><small>{product.category}</small></Link>
                    </div>
                    <h1 className="mb-3">{product.name}</h1>
                    <div className="mb-3">
                      <small className="text-warning">
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-half" />
                      </small>
                      <span className="ms-2">{product.rating}</span>
                      <span className="text-muted ms-1">({product.reviews} avis)</span>
                    </div>
                    <div className="mb-4"><span className="h2 text-dark me-2">{product.priceLabel}</span></div>
                    <p className="lead">
                      Produit issu du catalogue admin Almoune. Le prix, le stock et le type de livraison viennent maintenant des donnees importees dans l'application.
                    </p>

                    <div className="border rounded-3 p-4 bg-light mt-4">
                      <div className="row g-3">
                        <div className="col-sm-6">
                          <strong>Disponibilite</strong>
                          <p className="mb-0 text-muted">{product.stock > 0 ? `En stock (${product.stock})` : "Rupture de stock"}</p>
                        </div>
                        <div className="col-sm-6">
                          <strong>Livraison</strong>
                          <p className="mb-0 text-muted">{getDeliveryRuleLabel(product.type)}</p>
                        </div>
                        <div className="col-sm-6">
                          <strong>Reference</strong>
                          <p className="mb-0 text-muted">{product.sku || "Non renseignee"}</p>
                        </div>
                        <div className="col-sm-6">
                          <strong>Type</strong>
                          <p className="mb-0 text-muted text-uppercase">{product.type}</p>
                        </div>
                        {isWeighted ? (
                          <>
                            <div className="col-sm-6">
                              <strong>Poids minimum</strong>
                              <p className="mb-0 text-muted">
                                {formatProductAmount(
                                  product,
                                  product.minWeightKg || getDefaultCartAmountForProduct(product)
                                )}
                              </p>
                            </div>
                            <div className="col-sm-6">
                              <strong>Palier</strong>
                              <p className="mb-0 text-muted">
                                {formatProductAmount(product, quantityStep)}
                              </p>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-4 d-flex align-items-center gap-3 flex-wrap">
                      <div className="input-group" style={{ width: "140px" }}>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() =>
                            setQuantity((value) =>
                              Number(
                                Math.max(
                                  isWeighted
                                    ? product.minWeightKg || getDefaultCartAmountForProduct(product)
                                    : 1,
                                  Number(value || 0) - quantityStep
                                ).toFixed(3)
                              )
                            )
                          }
                        >
                          -
                        </button>
                        <input
                          type="text"
                          className="form-control text-center"
                          value={isWeighted ? formatProductAmount(product, quantity) : quantity}
                          readOnly
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() =>
                            setQuantity((value) => Number((Number(value || 0) + quantityStep).toFixed(3)))
                          }
                        >
                          +
                        </button>
                      </div>
                      <button type="button" className="btn btn-primary btn-lg" onClick={() => handleAddToCart(product, quantity)}>Ajouter au panier</button>
                      <Link to="/ShopWishList" className="btn btn-outline-dark btn-lg">Favoris</Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="my-lg-14 my-8">
              <div className="container">
                <div className="row">
                  <div className="col-12 mb-6">
                    <h2 className="mb-0">Produits associes</h2>
                  </div>
                </div>
                <div className="row g-4 row-cols-xl-4 row-cols-lg-3 row-cols-2 row-cols-md-2">
                  {relatedProducts.map((item) => (
                    <div className="col" key={item.id}>
                      <div className="card card-product">
                        <div className="card-body">
                          <div className="text-center position-relative">
                            <Link to="/SingleShop">
                              <img src={item.image} alt={item.name} className="mb-3 img-fluid" />
                            </Link>
                          </div>
                          <div className="text-small mb-1">
                            <Link to="/Shop" className="text-decoration-none text-muted"><small>{item.category}</small></Link>
                          </div>
                          <h2 className="fs-6">
                            <Link to="/SingleShop" className="text-inherit text-decoration-none">{item.name}</Link>
                          </h2>
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div><span className="text-dark">{item.priceLabel}</span></div>
                                    <div><button type="button" className="btn btn-primary btn-sm" onClick={() => handleAddToCart(item, getSuggestedCartAmount(item))}>Ajouter</button></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default SingleShop;
